import {
  DraftStringInput,
  InlineMarkupType,
  MessageFormatType,
  StringVariant,
} from 'src/features/string-group-detail/types';

/**
 * Everything an edit needs beyond the values shown in the modal: the draft's
 * storage identity plus the **base** (non-variant) snapshot of the string.
 *
 * A variant edit must leave the base text/note untouched and rewrite only its
 * own entry in `variants`, so these fields always come from the base
 * (no-`cxId`) describeString response — never from the variant-resolved one,
 * whose `sourceText` is the variant's text and whose `variants` is null.
 */
export interface EditDraftContext {
  namespaceId: string;
  stringGroupId: string;
  stringId: string;
  sourceLocale: string;
  /** Server version this edit is branched from; becomes the draft baseVersion. */
  stringGroupVersion: string;
  /** Base source text — carried through unchanged by a variant edit. */
  baseSourceText: string;
  /** Base context note — carried through unchanged by a variant edit. */
  baseContextNote?: string | null;
  /** Existing CX variants, from the base response. */
  variants: StringVariant[];
  /**
   * Absent when TRANSCOM reports an UNKNOWN format. Absence is carried through
   * as absence: a caller that did not let the user pick a format omits the field
   * from the draft change, so an unknown format is never overwritten with an
   * invented one.
   */
  inlineMarkupType?: InlineMarkupType | null;
  messageFormatType?: MessageFormatType | null;
}

/**
 * The server-reported state of the whole string, used by `applyFieldEdit` only
 * when the group draft has no entry for it yet.
 *
 * The format fields are normalized null → undefined rather than defaulted:
 * `JSON.stringify` drops an undefined key, so an unknown format is stored as
 * absent, exactly as the server reports it.
 *
 * Shared by every string-detail write path so they cannot drift apart.
 */
export function buildServerSnapshot(
  context: EditDraftContext,
): DraftStringInput {
  return {
    stringId: context.stringId,
    sourceLocale: context.sourceLocale,
    sourceText: context.baseSourceText,
    contextNote: context.baseContextNote ?? undefined,
    inlineMarkupType: context.inlineMarkupType ?? undefined,
    messageFormatType: context.messageFormatType ?? undefined,
    variants: context.variants,
  };
}

/**
 * Parses the API's stringified group version into the draft's
 * optimistic-concurrency token. A non-numeric value falls back to 0 (which the
 * group page reports as a stale draft) rather than storing NaN.
 */
export function parseBaseVersion(stringGroupVersion: string): number {
  const parsed = parseInt(stringGroupVersion, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}
